import { useCallback, useMemo } from 'react';
import { isEmpty, noop } from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector, useOnUnmount } from 'app/lib/hooks';
import { Field, Form, Yup } from 'app/components/core/Form';
import { Textarea } from 'app/components/core/Textarea';
import { FieldLayout } from 'app/components/core/FieldLayout';
import { Fieldset } from 'app/components/core/Fieldset';
import { LayoutDirection } from 'app/lib/constants';
import {
    doResetSetServiceAccount,
    doSetServiceAccount,
    selectServiceAccountsData,
    selectSetServiceAccountIsProcessing
} from 'app/store/jobs';
import { useDialogContext } from 'app/components/features/DialogWithChapters';
import { CredentialsSchema } from 'app/lib/schemas/SnowflakeConnectionSchema.js';
import { PrivateKeySelector } from 'app/components/features/PrivateKeySelector';
import { NameField } from './NameField.jsx';
import { UsernameField } from './UsernameField.jsx';

function getSchema(existingNames, currentName) {
    return Yup.object({
        uid: Yup.string().notRequired(),
        credentials: CredentialsSchema.label('Snowflake Identity')
            .meta({ summary: true })
            .required(),
        name: Yup.string()
            .label('Service Account Name')
            .meta({ summary: true })
            .trim()
            .required()
            .test(
                'Duplicate Service Account Names',
                'A service account with this name already exists',
                (value) => {
                    const lower = value?.toLowerCase();
                    const names = currentName
                        ? existingNames.filter((n) => n.toLowerCase() !== currentName.toLowerCase())
                        : existingNames;
                    return !names.some((n) => n.toLowerCase() === lower);
                }
            ),
        description: Yup.string().label('Description').meta({ summary: true }).notRequired()
    }).label('Service Account');
}

const getInitialValues = (env) =>
    isEmpty(env)
        ? {}
        : {
              uid: env.uid,
              credentials: {
                  username: env.username,
                  password: null
              },
              name: env.name,
              description: env.description
          };

export function CreateServiceAccountForm({ children, account, onSuccess = noop }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isProcessing = useSelector(selectSetServiceAccountIsProcessing);
    const serviceAccounts = useSelector(selectServiceAccountsData);
    const existingNames = useMemo(() => serviceAccounts.map((sa) => sa.name), [serviceAccounts]);
    const schema = useMemo(
        () => getSchema(existingNames, account?.name),
        [existingNames, account?.name]
    );
    const initialValues = getInitialValues(account ?? {});
    const { onUpdateAccount = onSuccess, onUpdateAccountSummary } = useDialogContext();

    useOnUnmount(() => {
        dispatch(doResetSetServiceAccount);
    });

    const handleSubmit = useCallback(
        ({ credentials, name, description }) => {
            const params = {
                name,
                description,
                credentials: {
                    type: 'PRIVATE_KEY',
                    user: credentials.username,
                    private_key: credentials.privateKey
                }
            };

            if (credentials.keyPassword) {
                params.credentials.private_key_password = credentials.keyPassword;
            }

            dispatch(doSetServiceAccount(params, onUpdateAccount));
        },
        [dispatch, onUpdateAccount]
    );

    return (
        <Form
            schema={schema}
            initialValues={initialValues}
            isSubmitting={isProcessing}
            onUpdateSummary={onUpdateAccountSummary}
            onSubmit={handleSubmit}>
            <FieldLayout>
                <Fieldset
                    direction={LayoutDirection.COLUMN}
                    label={t('jobs:dialogs.environment.account')}>
                    <UsernameField />
                    <PrivateKeySelector namePrefix="credentials" />
                </Fieldset>
                <Fieldset
                    direction={LayoutDirection.COLUMN}
                    label={t('jobs:dialogs.serviceAccount.attributes')}>
                    <NameField />
                    <Field
                        component={Textarea}
                        name="description"
                        maxChars={400}
                        label={t('common:description_optional')}
                        placeholder={t('jobs:dialogs.serviceAccount.descriptionPlaceholder')}
                    />
                </Fieldset>
                <Field component="input" type="hidden" name="uid" />
            </FieldLayout>
            {children}
        </Form>
    );
}
